import { Body, Button, IconButton } from '@assembly-js/design-system';
import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import React from 'react';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ModalWrapper } from 'src/legacy/components/Modals';
import type { ModalWrapperProps } from 'src/legacy/components/Modals/ModalWrapper';
import { getStudioPlanTitle } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import { formatDollars } from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import type { SellableStudioPlanId } from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import type { ExtraAppPreviewResponse } from 'src/services/api/studioCreditApi';

import { type AppLimitApp, AppLimitAppCard } from './AppLimitAppCard';

/** The single way past the limit this plan has. */
export type AppLimitRoute = 'fee' | 'upgrade';

/** Which action the limit interrupted; decides the copy's verb. */
export type AppLimitSurface = 'build' | 'add';

interface AppLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Takes the route the modal offered — pay the per-app fee, or upgrade. */
  onConfirm: () => void;
  /** Paying per app on a paid plan, or upgrading off Free. */
  route: AppLimitRoute;
  /** Plan to pitch on the upgrade route, if anything sits above. */
  upgradeTarget?: SellableStudioPlanId;
  /**
   * Stripe's quote for one more app: the recurring per-app amount and whatever
   * is prorated for the rest of this period. Undefined while it is in flight.
   */
  feePreview?: ExtraAppPreviewResponse;
  /**
   * Whether the quote came back unusable. Confirming still charges the correct
   * amount — the server prices it, not this quote — so an unquotable workspace
   * is let through with the price left unsaid rather than held behind a CTA that
   * can never enable.
   */
  isFeeUnquoted?: boolean;
  /**
   * Re-requests the quote. Given when the caller can retry: the price is worth
   * one more attempt before the user commits without seeing it.
   */
  onRetryFee?: () => void;
  /**
   * Whether the per-app charge is in flight. The modal stays open and shows the
   * CTA loading — the charge has to settle before the work it pays for starts,
   * so closing early would hide the only progress the user can see.
   */
  isConfirming?: boolean;
  /**
   * Whether the charge was refused. Keeps the user here with a reason and the
   * CTA live to retry, rather than closing on a failure.
   */
  hasChargeFailed?: boolean;
  /**
   * Whether the upgrade can actually be submitted yet — false while the plans
   * catalog or the current subscription is still resolving. Keeps the CTA from
   * dropping a click that has no payload to send.
   */
  isUpgradeReady?: boolean;
  /** The action being held back. */
  surface: AppLimitSurface;
  /** The app the paywall interrupted, shown as its own card. */
  app?: AppLimitApp;
}
const MODAL_WIDTH_PX = 344;
const APP_LIMIT_MODAL_CLASS = [
  '[&_.MuiDialog-paper]:rounded-lg',
  '[&_.MuiDialog-paper]:shadow-popover-050',
].join(' ');

/** Stripe's recurring per-app amount, e.g. "$8/month". */
const formatFee = (preview: ExtraAppPreviewResponse): string =>
  t(($) => $.page_apps.app_builder.app_limit_modal.fee, {
    amount: formatDollars(preview.recurringAmount),
    interval:
      preview.interval === 'annual'
        ? t(($) => $.page_apps.app_builder.app_limit_modal.interval.year)
        : t(($) => $.page_apps.app_builder.app_limit_modal.interval.month),
  });

/** The line under the title: the route out, priced where a quote exists. */
const getDescription = (
  surface: AppLimitSurface,
  route: AppLimitRoute,
  fee: string | undefined,
): string => {
  if (route === 'upgrade') {
    return surface === 'build'
      ? t(
          ($) =>
            $.page_apps.app_builder.app_limit_modal.description.upgrade_build,
        )
      : t(
          ($) =>
            $.page_apps.app_builder.app_limit_modal.description.upgrade_add,
        );
  }
  if (!fee) {
    return surface === 'build'
      ? t(
          ($) =>
            $.page_apps.app_builder.app_limit_modal.description.build_unpriced,
        )
      : t(
          ($) =>
            $.page_apps.app_builder.app_limit_modal.description.add_unpriced,
        );
  }
  return surface === 'build'
    ? t(($) => $.page_apps.app_builder.app_limit_modal.description.build, {
        fee,
      })
    : t(($) => $.page_apps.app_builder.app_limit_modal.description.add, {
        fee,
      });
};

const getFeeCta = (surface: AppLimitSurface): string =>
  surface === 'build'
    ? t(($) => $.page_apps.app_builder.app_limit_modal.cta.build)
    : t(($) => $.page_apps.app_builder.app_limit_modal.cta.add);

/**
 * Paywall shown when adding an app would take the workspace past its plan's app
 * allowance. Shared by both surfaces that can reach the limit: approving a build
 * and adding a marketplace app.
 *
 * One route out, not a choice of two: every Studio tier includes the same number
 * of apps, so upgrading buys no extra app — what it buys is the ability to pay
 * per app at all. Paid plans are therefore offered the per-app fee, and Free (the
 * only plan the backend hard-blocks from extra apps) is offered the upgrade that
 * unlocks it.
 */
export const AppLimitModal: React.FC<AppLimitModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  route,
  upgradeTarget,
  feePreview,
  isFeeUnquoted = false,
  onRetryFee,
  isConfirming = false,
  hasChargeFailed = false,
  isUpgradeReady = true,
  surface,
  app,
}) => {
  const isMobile = useMediaQuery('sm', 'max');

  // Free names the limit it hit — the plan's own, not a per-workspace allowance —
  // because the way past it is a different plan rather than a fee.
  const title =
    route === 'upgrade'
      ? t(($) => $.page_apps.app_builder.app_limit_modal.title_free)
      : t(($) => $.page_apps.app_builder.app_limit_modal.title);
  // Undismissable mid-charge: the request is already with Stripe, so closing
  // would strand the user not knowing whether they had been billed.
  const handleWrapperClose: ModalWrapperProps['onClose'] = () => {
    if (isConfirming) return;
    onClose();
  };

  const isFeeRoute = route === 'fee';
  // RTK Query keeps the last good quote in `feePreview` when a later refresh
  // errors, so a stale price can arrive alongside isFeeUnquoted. Once the quote
  // is unusable, drop it so the modal never shows a price Stripe has moved off of.
  const quote = isFeeUnquoted ? undefined : feePreview;
  // Drives the skeleton: a quote is on its way and has neither landed nor failed.
  const isFeeQuotePending = isFeeRoute && !quote && !isFeeUnquoted;
  // The fee CTA commits to a real charge, so it enables only with a quote on
  // screen — never on a pending or errored one, so the user never confirms a
  // price they were not shown. An unusable quote is not a dead end: the fee_error
  // row offers a Retry that re-requests it.
  const isReady = isFeeRoute ? quote !== undefined : isUpgradeReady;
  // The close affordance goes too: the charge is in flight and there is nothing
  // useful to do but wait for it.
  const isLocked = isFeeRoute && isConfirming;
  const cta = isFeeRoute
    ? getFeeCta(surface)
    : t(($) => $.page_apps.app_builder.app_limit_modal.cta.upgrade, {
        plan: upgradeTarget ? getStudioPlanTitle(upgradeTarget) : '',
      });
  // Free with nothing above it cannot happen (Free always has Pro to step up
  // to), but a route that cannot be submitted must not render a dead button.
  const hasRoute = isFeeRoute || upgradeTarget !== undefined;

  return (
    <ModalWrapper
      open={isOpen}
      onClose={handleWrapperClose}
      hideActions
      hideDividers
      disableGutters
      fullWidth={isMobile}
      width={isMobile ? undefined : MODAL_WIDTH_PX}
      aria-label={title}
      className={APP_LIMIT_MODAL_CLASS}
    >
      <div className="flex flex-col gap-5 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Body
              tag="div"
              size="lg"
              className="font-medium text-black-heading"
            >
              {title}
            </Body>
            {/* The price is Stripe's rather than a constant: the catalog owns the
                per-app amount and the interval follows the subscription. */}
            {isFeeQuotePending ? (
              <Skeleton variant="text" width="80%" />
            ) : (
              <Body tag="div" className="leading-[22px] text-gray-small">
                {getDescription(
                  surface,
                  route,
                  quote ? formatFee(quote) : undefined,
                )}
              </Body>
            )}
          </div>
          <IconButton
            icon="Close"
            variant="minimal"
            size="sm"
            aria-label={t(($) => $.page_apps.app_builder.app_limit_modal.close)}
            onClick={onClose}
            disabled={isLocked}
            className="shrink-0 rounded hover:bg-gray-150"
          />
        </div>

        {app && <AppLimitAppCard app={app} />}

        {hasRoute && (
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="sm"
              label={cta}
              className="box-border w-full"
              loading={isLocked}
              disabled={!isReady || isLocked}
              onClick={onConfirm}
            />
            {/* A refused charge means nothing was started, so the CTA stays live
                and this says why the user is still here. */}
            {isFeeRoute && hasChargeFailed && !isConfirming && (
              <Body tag="div" size="sm" className="text-center text-red-600">
                {t(
                  ($) => $.page_apps.app_builder.app_limit_modal.charge_failed,
                )}
              </Body>
            )}
            {/* Says why no price is shown. Keyed off isFeeUnquoted alone, not
                the raw feePreview: RTK Query can retain a stale quote through a
                failed refresh, and gating on its absence would hide this error —
                and its retry — exactly when the price on screen is out of date.
                The CTA stays live — the charge is raised on confirm either way —
                but the user gets one attempt at seeing the number first. */}
            {isFeeRoute && isFeeUnquoted && !hasChargeFailed && (
              <div className="flex items-center justify-center gap-1">
                <Body tag="div" size="sm" className="text-gray-small">
                  {t(($) => $.page_apps.app_builder.app_limit_modal.fee_error)}
                </Body>
                {onRetryFee && (
                  <Button
                    variant="text"
                    size="sm"
                    label={t(
                      ($) => $.page_apps.app_builder.app_limit_modal.fee_retry,
                    )}
                    onClick={onRetryFee}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};
